import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { referralAPI, signaturesAPI } from '../utils/api';
import { X, FileCheck, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ESignatureModal from '../components/ESignatureModal';

const ReferralAgreement = ({ onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [agreementId, setAgreementId] = useState(null);
  const [merchantId, setMerchantId] = useState(null);
  const [formData, setFormData] = useState({
    referrer_name: user?.company_name || '',
    referrer_address: '',
    referrer_email: user?.email || '',
    referrer_phone: user?.phone || '',
    referrer_signatory_name: user?.full_name || '',
    referrer_signatory_title: '',
    effective_date: new Date().toISOString().split('T')[0],
    confidentiality_years: 2,
    jurisdiction_county: 'Laramie',
    // Merchant Information
    merchant_signer_name: '',
    merchant_url: '',
    merchant_company_name: '',
    merchant_contact_name: '',
    merchant_contact_email: '',
    merchant_contact_phone: '',
    merchant_contact_address: '',
    merchant_bank_name: '',
    merchant_bank_address: '',
    merchant_bank_phone: '',
    merchant_bank_previous_contact: '',
    held_reserves_amount: '',
    processor_name: '',
    mid_application_reference: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create the referral agreement
      const agreementData = {
        referrer_name: formData.referrer_name,
        referrer_address: formData.referrer_address,
        referrer_email: formData.referrer_email,
        referrer_phone: formData.referrer_phone,
        referrer_signatory_name: formData.referrer_signatory_name,
        referrer_signatory_title: formData.referrer_signatory_title,
        effective_date: new Date(formData.effective_date).toISOString(),
        confidentiality_years: formData.confidentiality_years,
        jurisdiction_county: formData.jurisdiction_county
      };
      
      const agreementResponse = await referralAPI.createAgreement(agreementData);
      const agreementId = agreementResponse.data.agreement_id;
      
      // Submit the merchant referral with the agreement
      const merchantData = {
        referral_agreement_id: agreementId,
        merchant_signer_name: formData.merchant_signer_name,
        merchant_url: formData.merchant_url || '',
        merchant_company_name: formData.merchant_company_name,
        merchant_contact_name: formData.merchant_contact_name,
        merchant_contact_email: formData.merchant_contact_email,
        merchant_contact_phone: formData.merchant_contact_phone,
        merchant_contact_address: formData.merchant_contact_address,
        merchant_bank_name: formData.merchant_bank_name,
        merchant_bank_address: formData.merchant_bank_address,
        merchant_bank_phone: formData.merchant_bank_phone,
        merchant_bank_previous_contact: formData.merchant_bank_previous_contact,
        held_reserves_amount: parseFloat(formData.held_reserves_amount),
        processor_name: formData.processor_name,
        mid_application_reference: formData.mid_application_reference
      };
      
      const merchantResponse = await referralAPI.submitMerchant(merchantData);
      setAgreementId(agreementId);
      setMerchantId(merchantResponse.data?.merchant_id || merchantResponse.data?.referral_id);
      toast.success('Referral Agreement and Merchant Information submitted successfully!');
      setStep(4); // Show success screen with signature option
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit referral');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (step === 3) {
      navigate('/dashboard');
    }
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Referral Agreement</h2>
            <p className="text-sm text-slate-600 mt-1">10% Commission on All Recovered Funds</p>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600"
            data-testid="close-agreement-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Step Indicator */}
        {step < 4 && (
          <div className="flex justify-center gap-2 p-4 bg-slate-50 border-b border-slate-200">
            <div className={`w-3 h-3 rounded-full ${step === 1 ? 'bg-purple-600' : 'bg-slate-300'}`}></div>
            <div className={`w-3 h-3 rounded-full ${step === 2 ? 'bg-purple-600' : 'bg-slate-300'}`}></div>
            <div className={`w-3 h-3 rounded-full ${step === 3 ? 'bg-purple-600' : 'bg-slate-300'}`}></div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Agreement Terms</h3>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 max-h-96 overflow-y-auto mb-6">
                <div className="space-y-4 text-sm text-slate-700">
                  <div>
                    <h4 className="font-bold text-lg mb-2">REFERRAL AGREEMENT</h4>
                    <p className="text-xs text-slate-600 mb-4">Between RESERVE RELEASE AI, LLC and Referrer</p>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">RECITALS</h5>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Company possesses specialized expertise, legal resources (licensed in 48 US states), and an AI-driven legal model to recover held reserves from Merchant Banks for closed, medium-to-high-risk merchant accounts.</li>
                      <li>Referrer is an Independent Sales Organization (ISO) with clients (Merchants) that have funds ("Held Reserves") currently withheld by Merchant Banks.</li>
                      <li>Referrer desires to refer such Merchants to Company in exchange for a Referral Fee, and Company is willing to accept such referrals under the terms and conditions set forth herein.</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">KEY TERMS</h5>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded border border-slate-200">
                        <div className="font-semibold text-purple-600">Referral Fee</div>
                        <div>A flat <span className="font-bold">ten percent (10%)</span> of the Gross Recovered Funds for each successful recovery.</div>
                      </div>
                      <div className="bg-white p-3 rounded border border-slate-200">
                        <div className="font-semibold text-purple-600">Payment Terms</div>
                        <div>Fee payable within <span className="font-bold">thirty (30) days</span> of Company receiving the Gross Recovered Funds from the Merchant Bank.</div>
                      </div>
                      <div className="bg-white p-3 rounded border border-slate-200">
                        <div className="font-semibold text-purple-600">No Recovery, No Fee</div>
                        <div>If Company fails to recover any Held Reserves, no Referral Fee shall be due or payable.</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">REFERRAL PROCESS</h5>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li><strong>Submission:</strong> Referrer shall submit a written Referral Form including merchant's signer name, URL, identity documents, contact information, Merchant Bank details, and approximate amount of Held Reserves.</li>
                      <li><strong>Acceptance:</strong> Company has sole right to accept or reject any referral within 15 business days. No fee is payable for merchants already engaged with Company or rejected.</li>
                      <li><strong>Recovery Process:</strong> Company shall have sole control over the recovery process, including all legal strategy, negotiations, and settlements.</li>
                    </ol>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">INDEPENDENT CONTRACTOR STATUS</h5>
                    <p>Referrer is an independent contractor. Nothing in this Agreement creates an employer-employee, partnership, joint venture, or agency relationship. Referrer has no authority to bind Company or make representations on Company's behalf.</p>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">CONFIDENTIALITY</h5>
                    <p>Each Party agrees to keep all proprietary or confidential information received from the other Party strictly confidential. This obligation shall survive termination for a period of two (2) years.</p>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">TERM AND TERMINATION</h5>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Initial term of one (1) year, automatically renewing unless notice given 60 days prior.</li>
                      <li>Either Party may terminate for material breach with 30 days to cure.</li>
                      <li>Company remains obligated to pay Referral Fees for merchants referred during the Term, even if recovery is finalized after termination.</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-2">GOVERNING LAW</h5>
                    <p>This Agreement shall be governed by the laws of the State of Wyoming, with exclusive jurisdiction in Laramie County.</p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-4">
                    <p className="text-xs text-amber-900"><strong>Tax Responsibility:</strong> Referrer is solely responsible for all taxes arising from payment of the Referral Fee.</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 primary-button py-3"
                  data-testid="proceed-to-sign-btn"
                >
                  Proceed to Sign →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Referrer Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Referrer Name (Company/ISO) *</label>
                    <input
                      type="text"
                      name="referrer_name"
                      value={formData.referrer_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="referrer_email"
                      value={formData.referrer_email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Business Address *</label>
                  <input
                    type="text"
                    name="referrer_address"
                    value={formData.referrer_address}
                    onChange={handleChange}
                    placeholder="Street Address, City, State, ZIP"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="referrer_phone"
                      value={formData.referrer_phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Effective Date *</label>
                    <input
                      type="date"
                      name="effective_date"
                      value={formData.effective_date}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4 mt-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Authorized Signatory</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Signatory Name *</label>
                      <input
                        type="text"
                        name="referrer_signatory_name"
                        value={formData.referrer_signatory_name}
                        onChange={handleChange}
                        placeholder="Full Name"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                      <input
                        type="text"
                        name="referrer_signatory_title"
                        value={formData.referrer_signatory_title}
                        onChange={handleChange}
                        placeholder="e.g., CEO, Director, Owner"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                  <div className="flex gap-3">
                    <input
                      type="checkbox"
                      id="agree-terms"
                      required
                      className="mt-1"
                    />
                    <label htmlFor="agree-terms" className="text-sm text-slate-700">
                      I hereby acknowledge that I have read, understood, and agree to all terms and conditions set forth in this Referral Agreement. I understand that this creates a binding legal contract between my organization and reserverelease.AI, LLC.
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 primary-button py-3"
                  data-testid="proceed-to-merchant-btn"
                >
                  Next: Merchant Info →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit}>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Merchant Information</h3>
              <p className="text-sm text-slate-600 mb-6">Provide details about the merchant with frozen reserves you're referring to us.</p>
              
              <div className="space-y-6 mb-6">
                {/* Company & Signer Information */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Company & Signer Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company/Business Name *</label>
                      <input
                        type="text"
                        name="merchant_company_name"
                        value={formData.merchant_company_name}
                        onChange={handleChange}
                        placeholder="E.g., Premium Electronics LLC"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Merchant Signer Name *</label>
                      <input
                        type="text"
                        name="merchant_signer_name"
                        value={formData.merchant_signer_name}
                        onChange={handleChange}
                        placeholder="Legal signer from ISO MID Application"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Website URL</label>
                      <input
                        type="url"
                        name="merchant_url"
                        value={formData.merchant_url}
                        onChange={handleChange}
                        placeholder="https://merchantwebsite.com"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-2">MID Application Reference</label>
                      <input
                        type="text"
                        name="mid_application_reference"
                        value={formData.mid_application_reference}
                        onChange={handleChange}
                        placeholder="Reference number from ISO MID Application"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Merchant Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Person Name *</label>
                      <input
                        type="text"
                        name="merchant_contact_name"
                        value={formData.merchant_contact_name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Email *</label>
                      <input
                        type="email"
                        name="merchant_contact_email"
                        value={formData.merchant_contact_email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Phone *</label>
                      <input
                        type="tel"
                        name="merchant_contact_phone"
                        value={formData.merchant_contact_phone}
                        onChange={handleChange}
                        placeholder="+1-XXX-XXX-XXXX"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Business Address *</label>
                      <input
                        type="text"
                        name="merchant_contact_address"
                        value={formData.merchant_contact_address}
                        onChange={handleChange}
                        placeholder="Full business address"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Bank/Processor Information */}
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Merchant Bank & Processor Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Merchant Bank Name *</label>
                      <input
                        type="text"
                        name="merchant_bank_name"
                        value={formData.merchant_bank_name}
                        onChange={handleChange}
                        placeholder="E.g., Wells Fargo, Chase"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Processor Name *</label>
                      <input
                        type="text"
                        name="processor_name"
                        value={formData.processor_name}
                        onChange={handleChange}
                        placeholder="E.g., Stripe, PayPal, Square"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Bank Address *</label>
                      <input
                        type="text"
                        name="merchant_bank_address"
                        value={formData.merchant_bank_address}
                        onChange={handleChange}
                        placeholder="Full bank branch address"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Bank Phone *</label>
                      <input
                        type="tel"
                        name="merchant_bank_phone"
                        value={formData.merchant_bank_phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Previous Bank Contact (for ISO)</label>
                      <input
                        type="text"
                        name="merchant_bank_previous_contact"
                        value={formData.merchant_bank_previous_contact}
                        onChange={handleChange}
                        placeholder="Phone used for ISO communications"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Held Reserves Amount */}
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Frozen Reserves Information</h4>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Approximate Held Reserves Amount ($) *</label>
                    <input
                      type="number"
                      name="held_reserves_amount"
                      value={formData.held_reserves_amount}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="E.g., 75000.00"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Your 10% commission will be calculated on the actual recovered amount
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-amber-900">
                    <strong>Note:</strong> Please ensure all information matches the ISO MID Application documents. We will review and may request additional identity documents or verification before accepting this referral.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  disabled={loading}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  className="flex-1 primary-button py-3 disabled:opacity-50"
                  disabled={loading}
                  data-testid="submit-referral-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Referral'}
                </button>
              </div>
            </form>
          )}

          {step === 4 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileCheck className="text-green-600" size={40} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Agreement Submitted!</h3>
              <p className="text-slate-600 mb-6">Your referral agreement has been successfully submitted and is now active.</p>
              
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
                <h4 className="font-semibold text-slate-900 mb-3">Next Steps:</h4>
                <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                  <li>Sign your agreement electronically below</li>
                  <li>Our team will counter-sign and finalize the agreement</li>
                  <li>Track your referral status and commissions in the Referrals tab</li>
                  <li>Commissions are paid within 30 days of successful fund recovery</li>
                </ol>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setShowSignature(true)}
                  className="primary-button px-8 py-3 flex items-center gap-2"
                  data-testid="sign-agreement-btn"
                >
                  <PenTool size={20} />
                  Sign Agreement
                </button>
                <button
                  onClick={handleClose}
                  className="secondary-button px-8 py-3"
                  data-testid="close-success-btn"
                >
                  Sign Later
                </button>
              </div>
            </div>
          )}

          {/* E-Signature Modal */}
          {showSignature && merchantId && (
            <ESignatureModal
              isOpen={showSignature}
              onClose={() => setShowSignature(false)}
              documentId={merchantId}
              documentType="referral_agreement"
              documentTitle="Referral Agreement"
              defaultSignerName={formData.referrer_signatory_name}
              defaultSignerTitle={formData.referrer_signatory_title}
              defaultSignerEmail={formData.referrer_email}
              onSignComplete={() => {
                toast.success('Agreement signed successfully! Our team will counter-sign shortly.');
                handleClose();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralAgreement;
